/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EditableDataField } from '../../common/filter/editable-data-field';
import { CardsAndTotalCount, CardService, QueryOptions } from '../card.service';
import { InventoryCardsAndTotalCount, InventoryService } from '../../inventory/inventory.service';
import { CardSetsAndTotalCount, CardSetService } from '../card-set.service';
import { create as ImportParser } from './import-parser';
import { Card, Pagination } from '@deckplan.io/common';
import { AvailableColumn, CardSwapEvent } from '../../common/simple-table/simple-table.component';
import { PageEvent } from '@angular/material/paginator';
import { AlertsService } from '../../common/alerts/alerts.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface QtyAndCard {
    qty: number;
    card: Card;

    // This property is only used in "deck" mode
    importIntoInventory?: boolean;

    // This property is only used in "deck" mode
    addFromInventory?: boolean;
}

@Component({
    selector: 'app-importer',
    templateUrl: './importer.component.html',
    styleUrls: ['./importer.component.scss']
})
export class ImporterComponent implements OnInit {

    @Input() public columns: EditableDataField[] = [
        {dataType: 'number', field: 'quantity', title: 'Quantity', isInput: false},
        {dataType: 'string', field: 'name', title: 'Name', isInput: false}
    ];

    @Input() public delimiter = ' ';
    @Input() public mode: 'inventory' | 'deck' = 'inventory';
    @Input() public source = '';

    @Output() public cardsImported = new EventEmitter<QtyAndCard[]>();

    public errors = [];
    public cardsToImport: Card[] = [];
    public loadingData = false;
    public totalCount = 0;

    public defaultFoil = false;
    public defaultQuality = 'Played';

    public defaultOwned = false;
    public defaultImportIntoInventory = false;

    public cardColumns: AvailableColumn[] = [
        {selected: true, field: {title: 'Qty', dataType: 'number', field: 'quantity', isInput: false}},
        {selected: true, field: {title: 'Name', dataType: 'string', field: 'name', isInput: false}},
        {selected: true, field: {title: 'Set', dataType: 'cardset', field: 'set', isInput: false}}
    ];

    private editor: any;

    private readonly namesToId: any = {};
    private readonly idToQuality: any = {};
    private readonly idToIsFoil: any = {};
    private readonly idToImportIntoInv: any = {};
    private readonly idToOwned: any = {};

    constructor(private readonly cardService: CardService,
                private readonly inventoryService: InventoryService,
                private readonly setService: CardSetService,
                private readonly alertsService: AlertsService) { }

    public ngOnInit(): void {
        if (this.mode === 'inventory') {
            this.cardColumns = this.cardColumns.concat([
                {selected: true, field: {title: 'Is Foil?', dataType: 'boolean', field: 'isFoil', isInput: true}},
                {selected: true, field: {title: 'Quality', dataType: 'quality', field: 'quality', isInput: true}}
            ]);
        } else if (this.mode === 'deck') {
            this.cardColumns = this.cardColumns.concat([
                {selected: true, field: {title: 'Owned', dataType: 'boolean', field: 'owned', isInput: true}},
                {selected: true, field: {title: 'Add to Inv.', dataType: 'boolean', field: 'importIntoInventory', isInput: true}}
            ]);
        }
    }

    public onDefaultQualityChanged(value) {
        this.defaultQuality = value;
    }

    public getCardsToImport(): Observable<QtyAndCard[]> {
        const cardNames = this.validate();
        if (cardNames) {

            const nameToQty = cardNames.reduce((dict, namePair) => {
                dict[namePair.name] = namePair.qty;
                return dict;
            }, {});

            const ids = cardNames.reduce((aggr, pair) => {
                if (this.namesToId[pair.name]) {
                    aggr.push(this.namesToId[pair.name]);
                }
                return aggr;
            }, []);

            const names = cardNames.map(c => c.name);
            return this.cardService.getUniqueCardsByName({ids, names}, null)
                .pipe(map(cardsAndCount => {
                    if (cardsAndCount.totalCount === 0) {
                        this.alertsService.alert('No cards matched your parsed output!');
                    }
                    return cardsAndCount.cards.map(card => {
                        const quality = this.idToQuality[card.id] || this.defaultQuality;
                        const isFoil = this.idToIsFoil[card.id] || this.defaultFoil;
                        const importIntoInventory = this.idToImportIntoInv[card.id] || this.defaultImportIntoInventory;
                        const addFromInventory = this.idToOwned[card.id] || this.defaultOwned;
                        return {qty: nameToQty[card.name], card: {quality, isFoil, ...card}, importIntoInventory, addFromInventory};
                    });
                }));
        } else {
            return of([]);
        }
    }

    public previewImport(pagination: Pagination = {page: {pageSize: 5, pageIndex: 0}, sort: {active: 'name', direction: 'asc'}}) {
        const cardNames = this.validate();
        if (cardNames) {
            this.loadingData = true;

            const nameToQty = cardNames.reduce((dict, namePair) => {
                dict[namePair.name] = namePair.qty;
                return dict;
            }, {});

            const ids = cardNames.reduce((aggr, pair) => {
                if (this.namesToId[pair.name]) {
                    aggr.push(this.namesToId[pair.name]);
                }
                return aggr;
            }, []);

            // We don't need to de-dup as the backend will
            // prefer ids over names already
            const names = cardNames.map(c => c.name);

            this.cardService.getUniqueCardsByName({ids, names}, pagination)
                .subscribe(cardsAndCount => {
                    this.totalCount = cardsAndCount.totalCount;
                    this.cardsToImport = cardsAndCount.cards.map(card => {
                        const quality = this.idToQuality[card.id] || this.defaultQuality;
                        const isFoil = this.idToIsFoil[card.id] || this.defaultFoil;
                        const importIntoInventory = this.idToImportIntoInv[card.id] || this.defaultImportIntoInventory;
                        const addFromInventory = this.idToOwned[card.id] || this.defaultOwned;
                        card = {quantity: nameToQty[card.name], quality, isFoil, importIntoInventory, owned: addFromInventory, ...card} as any;
                        return card;
                    });
                    this.loadingData = false;

                    if (this.cardsToImport.length === 0) {
                        this.alertsService.alert('No cards matched your parsed output!');
                    }
                });
        } else {
            this.cardsToImport = [];
        }
    }

    public pageChanged(pageEvent: PageEvent) {
        this.previewImport({page: {pageSize: 5, pageIndex: pageEvent.pageIndex}, sort: {active: 'name', direction: 'asc'}});
    }

    public configuration() {
        return {
            base_url: '/tinymce',
            suffix: '.min',
            height: 300,
            menubar: false,
            save_enablewhendirty: false,
            plugins: ['searchreplace'],
            toolbar: ['previewparse'],
            force_br_newlines: false,
            force_p_newlines: false,
            forced_root_block: '',
            setup: (editor) => {
                this.editor = editor;

                editor.ui.registry.addButton('previewparse', {
                    text: 'Preview',
                    icon: 'preview',
                    onAction: () => this.previewImport()
                });

                const sanitizeFn = value => {
                    if (value.includes(this.delimiter) && this.delimiter !== ' ') {
                        value = `"${value}"`;
                    }
                    return value;
                };

                const getCardValue = card => {
                    const index = this.columns.findIndex(col => col.field === 'name');
                    return this.columns.slice(index).map(col => col.field).map(field => sanitizeFn(card[field])).join(this.delimiter);
                };

                const mapCardsToAutocomplete = (cards, resolve, isInv) => {
                    const sortedCards = cards.sort((a, b) => {
                        const bDate = new Date(b.releasedAt);
                        const aDate = new Date(a.releasedAt);
                        // @ts-ignore
                        return bDate - aDate;
                    });
                    resolve(sortedCards.map(card => ({
                        value: JSON.stringify({content: getCardValue(card), name: card.name, id: card.id, isInv}),
                        text: `${card.name} (${card.setName} - ${new Date(card.releasedAt).toLocaleDateString('en', {year: 'numeric', month: '2-digit'})})`,
                        icon: `<img style="width: 20px" src="${card.images.small}" />`
                    })));
                };

                const cardQueryOptions = pattern => ({
                    query: {dataField: {field: 'name', title: 'Name', dataType: 'string'}, operation: 'startsWith', value: pattern},
                    pagination: {page: {pageSize: 5, pageIndex: 0}, sort: {active: 'name', direction: 'asc'}}
                }) as QueryOptions;

                const onAction = (autocompleteApi, rng, value) => {
                    value = JSON.parse(value);
                    console.log('range', rng);
                    editor.selection.setRng(rng);
                    editor.insertContent(value.content);
                    this.namesToId[value.name] = value.id;
                    if (value.isInv) {
                        this.idToOwned[value.id] = true;
                    }
                    autocompleteApi.hide();
                };

                editor.ui.registry.addAutocompleter('cardnames', {
                    ch: '#',
                    minChars: 0,
                    columns: 1,
                    matches: () => true,
                    fetch: pattern => new Promise(resolve => {
                        this.cardService.getCards(cardQueryOptions(pattern)).subscribe((cards: CardsAndTotalCount) => {
                            mapCardsToAutocomplete(cards.cards, resolve, false);
                        });
                    }),
                    onAction
                });
                editor.ui.registry.addAutocompleter('inventorycardnames', {
                    ch: '@',
                    minChars: 2,
                    columns: 1,
                    matches: () => true,
                    fetch: pattern => new Promise(resolve => {
                        this.inventoryService.getInventory(cardQueryOptions(pattern)).subscribe((cards: InventoryCardsAndTotalCount) => {
                            mapCardsToAutocomplete(cards.inventoryCards, resolve, true);
                        });
                    }),
                    onAction
                });
                editor.ui.registry.addAutocompleter('setnames', {
                    ch: '!',
                    minChars: 0,
                    columns: 1,
                    fetch: pattern => new Promise(resolve => {
                        this.setService.getAll({page: {pageSize: 5, pageIndex: 0}, sort: {active: 'name', direction: 'asc'}},
                            {
                                operator: 'or', operands: [
                                    {dataField: {field: 'name', title: 'Name', dataType: 'string'}, operation: 'startsWith', value: pattern},
                                    {dataField: {field: 'code', title: 'Code', dataType: 'string'}, operation: 'startsWith', value: pattern}
                                ]
                            }
                        ).subscribe((sets: CardSetsAndTotalCount) => {
                            resolve(sets.cardSets.map(set => ({
                                value: set.code,
                                text: `${set.name} (${set.code})`,
                                icon: `<img style="width: 20px" src="${set.iconSvgUri}" />`
                            })));
                        });
                    }),
                    matches: () => true,
                    onAction
                });
            }
        };
    }

    public validate() {
        this.errors = [];
        const parser = ImportParser(this.delimiter, this.columns);
        try {
            return parser.parse(this.editor.getContent({format: 'text'}));
        } catch (error) {
            this.errors.push(error.message);
            return null;
        }
    }

    public swapCards(event: CardSwapEvent) {
        const oldIndex = this.cardsToImport.findIndex(card => card.id === event.oldCard.id);
        this.namesToId[event.newCard.name] = event.newCard.id;
        this.idToIsFoil[event.newCard.id] = this.idToIsFoil[event.oldCard.id] || this.defaultFoil;
        this.idToQuality[event.newCard.id] = this.idToQuality[event.oldCard.id] || this.defaultQuality;
        this.idToImportIntoInv[event.newCard.id] = this.idToImportIntoInv[event.oldCard.id] || this.defaultOwned;
        (event.newCard as any).isFoil = this.idToIsFoil[event.newCard.id];
        (event.newCard as any).quality = this.idToQuality[event.newCard.id];
        (event.newCard as any).importIntoInventory = this.idToImportIntoInv[event.newCard.id];
        this.cardsToImport[oldIndex] = event.newCard;
        this.cardsToImport = [...this.cardsToImport];
    }

    public fieldChanged(event) {
        if (event.dataField.field === 'quality') {
            this.idToQuality[event.data.id] = event.value;
        } else if (event.dataField.field === 'isFoil') {
            this.idToIsFoil[event.data.id] = event.value;
        } else if (event.dataField.field === 'importIntoInventory') {
            this.idToImportIntoInv[event.data.id] = event.value;
        } else if (event.dataField.field === 'owned') {
            this.idToOwned[event.data.id] = event.value;
        }
    }

}
